/**
 * ContentPlaceholderPage
 *
 * Shown when navigating to a doc article or runbook detail that does not yet
 * have authored content. Looks intentional rather than broken.
 *
 * @module help
 */

import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FileText, ArrowLeft, BookOpen, BookMarked, Clock } from 'lucide-react';

function toTitleCase(slug: string): string {
  return slug
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

type ContentType = 'article' | 'runbook';

interface ContentPlaceholderPageProps {
  type: ContentType;
  backPath: string;
  backLabel: string;
}

export const ContentPlaceholderPage: React.FC<ContentPlaceholderPageProps> = ({
  type,
  backPath,
  backLabel,
}) => {
  const params = useParams();
  const navigate = useNavigate();

  const rawId = params.articleId ?? params.categoryId ?? params.id ?? '';
  const title = toTitleCase(rawId);

  const Icon = type === 'runbook' ? BookMarked : FileText;
  const ParentIcon = type === 'runbook' ? BookMarked : BookOpen;

  return (
    <div className="p-6 max-w-2xl">
      {/* Back link */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        {backLabel}
      </button>

      {/* Icon + title */}
      <div className="flex items-start gap-4 mb-8">
        <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg shrink-0">
          <Icon className="w-7 h-7 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {title}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm">
            {type === 'runbook' ? 'Operational Runbook' : 'Documentation Article'}
          </p>
        </div>
      </div>

      {/* Placeholder notice */}
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-5 mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Clock className="w-4 h-4 text-amber-600 dark:text-amber-400" />
          <span className="font-medium text-amber-800 dark:text-amber-300 text-sm">
            Content in preparation
          </span>
        </div>
        <p className="text-sm text-amber-700 dark:text-amber-400">
          {type === 'runbook'
            ? 'This runbook procedure is being authored and will be available shortly. In the meantime, refer to the platform team for guidance on this operational scenario.'
            : 'This article is being written and will be published shortly. Check back soon or contact the platform team if you need this information urgently.'}
        </p>
      </div>

      {/* Back to index */}
      <Link
        to={backPath}
        className="inline-flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:underline"
      >
        <ParentIcon className="w-4 h-4" />
        Back to {backLabel}
      </Link>
    </div>
  );
};

export const RunbookPlaceholderPage: React.FC = () => (
  <ContentPlaceholderPage
    type="runbook"
    backPath="/help/runbooks"
    backLabel="Runbooks"
  />
);

export const DocArticlePlaceholderPage: React.FC = () => (
  <ContentPlaceholderPage
    type="article"
    backPath="/help/docs"
    backLabel="Documentation"
  />
);

export default ContentPlaceholderPage;
